import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { DropdownComponent } from '../../../../../../framework/inputs/dropdown/dropdown.component';
import { FloatingInputComponent } from '../../../../../../framework/inputs/floating-input/floating-input.component';
import { InputCalendarComponent } from '../../../../../../framework/inputs/input-calendar/input-calendar.component';
import {
  defaultLineItemsTotal,
  defaultMonthlyData,
  DISTRIBUTION_TYPES,
  distributionTypes,
  MONTHS_KEYS,
  SPEND_TYPES,
} from '../../../../../../framework/constants/spend.constants';
import { SpendCustomInputComponent } from '../../../../../../framework/inputs/spend-custom-input/spend-custom-input.component';
import {
  DistributionFieldType,
  ILineItemExtended,
  ISpendDistribution,
  ISubitemExtended,
} from '../../../../../../store/spend/spend.interfaces';
import {
  deleteLineItem,
  spendActionTypes,
  updateDistribution,
  updateLineItem,
} from '../../../../../../store/spend/spend.actions';
import { CurrentUserService } from '../../../../../../services/current-user.service';
import {
  getSelectedYear,
  selectHasCommitments,
} from '../../../../../../store/spend/spend.selectors';
import { SpendDistributionService } from '../../../../../../services/spend-distribution.service';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { TooltipDirective } from '@app/directives/tooltip.directive';

@Component({
  selector: 'app-budget-line-item',
  imports: [
    AsyncPipe,
    CdkDragHandle,
    DropdownComponent,
    FloatingInputComponent,
    InputCalendarComponent,
    SpendCustomInputComponent,
    NgClass,
    TooltipDirective,
    FormsModule,
  ],
  templateUrl: './budget-line-item.component.html',
  styleUrl: './budget-line-item.component.scss',
})
export class BudgetLineItemComponent implements OnInit, OnDestroy {
  protected readonly userService = inject(CurrentUserService);
  protected readonly store = inject(Store);
  protected readonly spendDistributionService = inject(SpendDistributionService);

  protected readonly distributionTypes = distributionTypes;
  protected readonly MONTHS_KEYS = MONTHS_KEYS;
  protected readonly DISTRIBUTION_TYPES = DISTRIBUTION_TYPES;
  protected readonly SPEND_TYPES = SPEND_TYPES;
  protected readonly totals = defaultLineItemsTotal;
  protected readonly DATE_FORMAT = 'YYYY-MM-DD';

  private _item: ILineItemExtended;
  @Input({ required: true }) set item(value: ILineItemExtended) {
    this._item = value;
    this.setDisabledMonth(value);
  }
  get item(): ILineItemExtended {
    return this._item;
  }

  @Input({ required: true }) minStartDate: string;
  @Input({ required: true }) canDelete: boolean;
  @Input() isSubItem = false;

  isDestroyed$: Subject<boolean> = new Subject<boolean>();
  hasCommitments$ = this.store.select(selectHasCommitments);
  hasPrimeContract$ = this.store.select(primeSelectors.selectHasPrimeContract);
  selectedYear$ = this.store.select(getSelectedYear);

  selectedYear = 0;

  durationMonths = Array(72)
    .fill(0)
    .map((_, i) => i + 1)
    .map((i) => {
      return {
        label: i === 1 ? `${i} month` : `${i} months`,
        value: i,
      };
    });

  ngOnInit(): void {
    this.selectedYear$.pipe(takeUntil(this.isDestroyed$)).subscribe((year) => {
      this.selectedYear = year;
      this.setDisabledMonth(this.item);
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  deleteLineFromStore(id: number) {
    this.store.dispatch(deleteLineItem({ id }));
  }

  setDisabledMonth(item: ILineItemExtended) {
    const availableMonths = this.spendDistributionService.getDisabledMonths(
      this.selectedYear,
      item,
      'monthly_budget',
    );

    // disable all months that should be non-editable
    Object.keys(item.monthly_data).forEach((monthKey, index) => {
      item.monthly_disable[monthKey] = !availableMonths[index]; // key starts with 1
    });
  }

  clearName(item: ILineItemExtended) {
    if (item.name === 'Project Cost') {
      item.name = '';
    }
  }

  showName(item: ILineItemExtended) {
    if (!item.name) {
      item.name = 'Project Cost';
      this.registerChangeName(item.name, item);
    }
  }

  registerChangeName(name: string, item: ILineItemExtended | ISubitemExtended) {
    const lineItem: ILineItemExtended = cloneDeep(item);
    lineItem.name = name;

    if (this.isSubItem) {
      this.store.dispatch(
        spendActionTypes.updateSubitem({
          subitem: this.spendDistributionService.extendedSubitemToOriginal(
            lineItem as ISubitemExtended,
          ),
        }),
      );
      return;
    }

    this.store.dispatch(
      updateLineItem({ lineItem: this.spendDistributionService.extendedItemToOriginal(lineItem) }),
    );
  }

  registerChange(
    inputValue: number,
    item: ILineItemExtended | ISubitemExtended,
    month: string,
    field: DistributionFieldType = 'monthly_budget',
  ) {
    const newValue = inputValue;
    const lineItem: ILineItemExtended = cloneDeep(item);
    let budget = lineItem.budget.find((el) => el.year === this.selectedYear);
    if (!budget) {
      budget = {
        year: this.selectedYear,
        monthly_prime: { ...defaultMonthlyData },
        monthly_budget: { ...defaultMonthlyData },
      };
      lineItem.budget.push(budget);
    }

    if (!isNaN(newValue)) {
      budget[field][month] = newValue;
    }

    lineItem.distribution = DISTRIBUTION_TYPES.MANUAL;

    if (this.isSubItem) {
      this.store.dispatch(
        spendActionTypes.updateSubitem({
          subitem: this.spendDistributionService.extendedSubitemToOriginal(
            lineItem as ISubitemExtended,
          ),
        }),
      );
      return;
    }

    this.store.dispatch(
      updateLineItem({ lineItem: this.spendDistributionService.extendedItemToOriginal(lineItem) }),
    );
  }

  setItemTotal(total: number, item: ILineItemExtended) {
    item.project_total = isNaN(total) ? 0 : total;
  }

  setStartDate(date: string, item: ILineItemExtended) {
    item.start_date = date;
  }

  setItemDuration(duration: number, item: ILineItemExtended) {
    item.duration = duration;
  }

  setItemDistribution(distribution: DISTRIBUTION_TYPES, item: ILineItemExtended) {
    item.distribution = distribution;
  }

  calcDistribution(item: ILineItemExtended | ISubitemExtended) {
    const distribution: ISpendDistribution = {
      distribution: item.distribution,
      start_date: dayjs(item.start_date).format(this.DATE_FORMAT),
      field: 'monthly_budget',
      duration: item.duration,
      budget: item.project_total,
    };
    this.setDisabledMonth(this.item);

    if (this.isSubItem) {
      this.store.dispatch(
        spendActionTypes.updateSubitemDistribution({
          parentId: (item as ISubitemExtended).parent_id,
          lineId: item.id,
          distribution,
        }),
      );
    } else {
      this.store.dispatch(updateDistribution({ lineId: item.id, distribution }));
    }
  }

  protected readonly AsyncPipe = AsyncPipe;
}
